import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import {
  useUpdateProcedureMutation,
  useFetchProceduresQuery,
} from 'features/Procedure/state/procedure.api'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { DrawingElementVisibleState, EGender } from 'features/Procedure/state/procedure.type'
import type { ProcedureDto } from 'features/Procedure/state/procedure-api.type'
import {
  serializeDrawingDescriptionForApi,
  sumUnitsMarksForProcedure,
} from 'features/Procedure/utils/drawingDescription.utils'

const DEFAULT_AUTOSAVE_DELAY_MS = 2000
const ENCOUNTER_SYNC_SETTLE_DELAY_MS = 150

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

type UseSaveDrawingOptions = {
  enableAutosave?: boolean
}

export const useSaveDrawing = ({ enableAutosave = false }: UseSaveDrawingOptions = {}) => {
  const { siteId } = useParams<{ siteId: string }>()
  const dispatch = useAppDispatch()
  const { setNotification } = useNotification()
  const [updateProcedure, { isLoading }] = useUpdateProcedureMutation({
    fixedCacheKey: 'autosaveDrawing',
  })
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [lastSaveTime, setLastSaveTime] = useState<number | null>(null)
  const [autosaveDelayMs, setAutosaveDelayMs] = useState(DEFAULT_AUTOSAVE_DELAY_MS)
  const [isEncounterSyncing, setIsEncounterSyncing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const isSavingRef = useRef(false)
  const lastSavedDrawingCountRef = useRef<number | null>(null)
  const prevEncounterUidRef = useRef<string | null>(null)

  const increaseAutosaveDelay = useCallback(() => {
    setAutosaveDelayMs((prev) => prev + 5000)
  }, [])

  const decreaseAutosaveDelay = useCallback(() => {
    setAutosaveDelayMs((prev) => Math.max(1000, prev - 5000))
  }, [])

  const drawingElements = useAppSelector((state) => state.procedureReducer.drawingElements)
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )
  const modelVersion = useAppSelector((state) => state.procedureReducer.modelVersion)
  const { data: proceduresData, isFetching: isProceduresFetching } = useFetchProceduresQuery(
    {
      siteId,
      encounterUid: selectedEncounterUid,
    },
    {
      skip: !selectedEncounterUid || !siteId,
    },
  )

  const hasRemoteModelVersion = !!proceduresData?.model_version

  const saveDrawing = useCallback(async () => {
    try {
      if (!siteId || !selectedEncounterUid) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: 'Missing site or encounter information',
        })
        return
      }

      if (isProceduresFetching) {
        return
      }

      if (!proceduresData?.items?.length) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: 'No procedures found',
        })
        return
      }

      const drawingsByProcedureId = new Map<number, typeof drawingElements>()
      // const visibleStates = [
      //   DrawingElementVisibleState.VISIBLE,
      //   DrawingElementVisibleState.PERSISTED,
      // ]
      drawingElements.forEach((element) => {
        if (
          element.procedureId

          // && visibleStates.includes(element.visibleState)) {
        ) {
          const existing = drawingsByProcedureId.get(element.procedureId) || []
          drawingsByProcedureId.set(element.procedureId, [...existing, element])
        }
      })

      const proceduresWithNewDrawings = proceduresData.items
      // keep this commented code for reference, atm we will save everything
      // proceduresData.items.filter((procedure) => {
      //   const procedureElements = drawingsByProcedureId.get(procedure.id) || []
      //   return procedureElements.some(
      //     (el) =>
      //       el.visibleState === DrawingElementVisibleState.VISIBLE ||
      //       (el.visibleState === DrawingElementVisibleState.PERSISTED && el.isModified),
      //   )
      // })

      if (proceduresWithNewDrawings.length === 0) {
        return
      }

      const procedureDtos: ProcedureDto[] = proceduresWithNewDrawings.map((procedure) => {
        const procedureElements = drawingsByProcedureId.get(procedure.id) || []
        const { sum, hasUnitMarks } = sumUnitsMarksForProcedure(drawingElements, procedure.id)

        return {
          ...procedure,
          procedure_units: hasUnitMarks
            ? (Math.round(sum * 100) / 100).toFixed(2)
            : procedure.procedure_units ?? '',
          drawings: procedureElements.map((el) => {
            const {
              procedureId: _procedureId,
              clientKey: _clientKey,
              isModified: _isModified,
              ...elementWithoutProcedureId
            } = el
            return {
              ...elementWithoutProcedureId,
              description: serializeDrawingDescriptionForApi(el.description, el.drawMode),
              // visibleState: DrawingElementVisibleState.PERSISTED,
            }
          }),
        }
      })

      const result = await updateProcedure({
        siteId,
        encounterUid: selectedEncounterUid,
        skipESignInvalidation: true,
        data: {
          procedures: procedureDtos,
          model_version: modelVersion,
          gender: modelVersion?.startsWith(EGender.FEMALE) ? EGender.FEMALE : EGender.MALE,
        },
      }).unwrap()

      if (result && typeof result === 'object' && 'success' in result) {
        const responseResult = result as { success: boolean; error?: string; statusCode?: number }
        if (!responseResult.success) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: responseResult.error || 'Failed to save drawings',
          })
          return
        }
      }
      lastSavedDrawingCountRef.current = drawingElements.length
    } catch (error: any) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: error?.data?.message || 'Failed to save drawings',
      })
    }
  }, [
    siteId,
    selectedEncounterUid,
    proceduresData,
    isProceduresFetching,
    drawingElements,
    updateProcedure,
    modelVersion,
    setNotification,
  ])

  useEffect(() => {
    if (prevEncounterUidRef.current === selectedEncounterUid) {
      return
    }

    prevEncounterUidRef.current = selectedEncounterUid
    lastSavedDrawingCountRef.current = null
    setIsEncounterSyncing(Boolean(selectedEncounterUid))
  }, [selectedEncounterUid])

  useEffect(() => {
    if (!selectedEncounterUid || !isEncounterSyncing || isProceduresFetching) {
      return
    }

    if (proceduresData?.items?.length === undefined) {
      return
    }

    const timer = setTimeout(() => {
      lastSavedDrawingCountRef.current = drawingElements.length
      setIsEncounterSyncing(false)
    }, ENCOUNTER_SYNC_SETTLE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [
    selectedEncounterUid,
    isEncounterSyncing,
    isProceduresFetching,
    proceduresData?.items?.length,
    drawingElements.length,
  ])

  const hasElementToSave = useMemo(() => {
    if (!proceduresData?.items?.length) {
      return false
    }

    const hasModified = drawingElements.some(
      (el) =>
        (el.visibleState === DrawingElementVisibleState.VISIBLE && el.isModified) ||
        (el.visibleState === DrawingElementVisibleState.PERSISTED && el.isModified),
    )
    const countChanged =
      !isEncounterSyncing &&
      lastSavedDrawingCountRef.current !== null &&
      drawingElements.length !== lastSavedDrawingCountRef.current
    return hasModified || countChanged
  }, [drawingElements, proceduresData?.items?.length, isEncounterSyncing])

  useEffect(() => {
    if (
      !enableAutosave ||
      !hasElementToSave ||
      isSaving ||
      isProceduresFetching ||
      isEncounterSyncing
    ) {
      return
    }

    const timer = setTimeout(async () => {
      if (isSavingRef.current) return

      isSavingRef.current = true
      setIsSaving(true)
      setSaveStatus('saving')

      try {
        await saveDrawing()
        setSaveStatus('saved')
        setLastSaveTime(Date.now())
      } catch {
        setSaveStatus('error')
      } finally {
        isSavingRef.current = false
        setIsSaving(false)
      }
    }, autosaveDelayMs)

    return () => clearTimeout(timer)
  }, [
    enableAutosave,
    hasElementToSave,
    isSaving,
    saveDrawing,
    dispatch,
    isProceduresFetching,
    isEncounterSyncing,
  ])

  const saveDrawingRef = useRef(saveDrawing)
  saveDrawingRef.current = saveDrawing
  const canFlushRef = useRef(false)
  canFlushRef.current =
    enableAutosave && hasElementToSave && !isProceduresFetching && !isEncounterSyncing

  useEffect(() => {
    if (!enableAutosave) {
      return
    }

    const flushPendingSave = () => {
      if (!canFlushRef.current) {
        return
      }
      void saveDrawingRef.current()
    }

    window.addEventListener('pagehide', flushPendingSave)
    window.addEventListener('beforeunload', flushPendingSave)

    return () => {
      window.removeEventListener('pagehide', flushPendingSave)
      window.removeEventListener('beforeunload', flushPendingSave)
    }
  }, [enableAutosave])

  useEffect(() => {
    if (saveStatus === 'saved') {
      const timer = setTimeout(() => {
        setSaveStatus('idle')
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [saveStatus])
  return {
    saveDrawing,
    isSaving: isLoading,
    isEncounterSyncing,
    hasElementToSave,
    hasRemoteModelVersion,
    saveStatus,
    lastSaveTime,
    autosaveDelayMs,
    increaseAutosaveDelay,
    decreaseAutosaveDelay,
  }
}
